/**
 * CI identities — ADR-0074 owner decision 2026-10-09, Settings → Security #ci-identities.
 *
 * Owners / admins trust the CI workflow that runs the workspace's verification checks: GitHub
 * repository + workflow file + branch. GAC Pro builds the exact identity it checks from those three
 * (no wildcards). Removing one stops trusting it (with a reason) and keeps it in the history.
 * The form opens prefilled from `?ci_repo=&ci_workflow=&ci_ref=` ("Trust this workflow" on a check
 * whose run wasn't trusted). Everyone else sees whom to ask (reads are owner / admin too).
 * V105-7-7 addendum: the query string is anyone's to craft (a link), so a trust is never one click:
 * the owner / admin confirms the exact repository, workflow file and full ref before it is sent.
 * V108-8-11 (ADR-0087 amendment 2026-10-14): a trusted workflow also signs CI gate results for the
 * workspace's systems; a run on the trusted branch then counts as CI gate evidence. Said once, in plain words.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Field, Input, Spinner } from '@aigovops/design-system';
import { ApiError } from '../lib/api';
import { createCiIdentity, disableCiIdentity, listCiIdentities, safeReturnTo, type CiIdentity } from '../lib/ci-identities-api';
import { fmtDate } from '../lib/format';
import { AskAdmin } from './AskAdmin';
import './data-protection-settings.css';

// V108-8-11 (critics H): the gate workflow `gac init` writes; a trust covers this exact file only.
const DEFAULT_WORKFLOW = '.github/workflows/governance.yml';
/** A pull-request ref never runs on the default branch: prefill the default branch instead, and say so. */
const PR_REF = /^refs\/pull\//;
type Fields = { repository: string; workflow_path: string; ref: string };

export function CiIdentitySettings({ slug, role }: { slug: string; role: string }) {
  const manage = role === 'owner' || role === 'admin';
  const qc = useQueryClient();
  const { search } = useLocation();
  const q = useQuery({ queryKey: ['ci-identities', slug], queryFn: () => listCiIdentities(slug), enabled: manage, retry: false });
  const params = new URLSearchParams(search);
  const prefill = params.has('ci_repo') || params.has('ci_workflow');
  const prefRef = params.get('ci_ref');
  const refSwapped = !!prefRef && PR_REF.test(prefRef);
  const [form, setForm] = useState<Fields>({ repository: params.get('ci_repo') ?? '', workflow_path: params.get('ci_workflow') ?? DEFAULT_WORKFLOW, ref: prefRef && !refSwapped ? prefRef : 'main' });
  const [adding, setAdding] = useState(prefill);
  const [errors, setErrors] = useState<Partial<Fields>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState<Fields | null>(null);
  const [reasonError, setReasonError] = useState('');
  // Round 3 (F-H1): after a trust that came from a check, say to run the workflow again and offer the way back.
  const [done, setDone] = useState<string | null>(null);
  const returnTo = safeReturnTo(slug, params.get('return_to'));
  // Round 3 (A-H3): focus moves to a named element after the next render.
  const focusNext = useRef<string | null>(null);
  useEffect(() => {
    const id = focusNext.current;
    if (!id) return;
    const el = document.getElementById(id);
    if (el) { focusNext.current = null; el.focus(); }
  });
  useEffect(() => { if (prefill && manage && q.isSuccess) window.setTimeout(() => document.getElementById('ci-id-repo')?.focus(), 0); }, [prefill, manage, q.isSuccess]);

  if (!manage) {
    return (
      <div id="ci-identities" className="dps" tabIndex={-1} role="region" aria-labelledby="ci-id-h">
        <Card><div className="dps-body">
          <h2 id="ci-id-h" className="dps-h">CI identities</h2>
          <p className="dps-dim">The CI workflows GAC Pro trusts to run your verification checks and sign your CI gate results. An owner or admin manages them.</p>
          <AskAdmin slug={slug} what="trust our CI workflow for verification checks and CI gate results" subject="CI identities in GAC Pro" compact />
        </div></Card>
      </div>
    );
  }

  const say = (t: string) => { setMsg(''); window.setTimeout(() => setMsg(t), 60); };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const missing: Partial<Fields> = {};
    if (!form.repository.trim()) missing.repository = 'Enter the repository as owner/name, e.g. acme/infra.';
    if (!form.workflow_path.trim()) missing.workflow_path = 'Enter the workflow file path.';
    if (!form.ref.trim()) missing.ref = 'Enter the branch, e.g. main.';
    setErrors(missing);
    const first = (['repository', 'workflow_path', 'ref'] as const).find((k) => missing[k]);
    if (first) { document.getElementById(`ci-id-${first === 'repository' ? 'repo' : first === 'workflow_path' ? 'wf' : 'ref'}`)?.focus(); return; }
    setErr(null);
    setConfirm({ repository: form.repository.trim(), workflow_path: form.workflow_path.trim(), ref: form.ref.trim() });
    window.setTimeout(() => document.getElementById('ci-id-confirm-h')?.focus(), 0);
  };
  const trust = async (values: Fields) => {
    if (busy) return;
    setBusy('add'); setErr(null);
    try {
      const r = await createCiIdentity(slug, values);
      await qc.invalidateQueries({ queryKey: ['ci-identities', slug] });
      setConfirm(null);
      setAdding(false);
      const text = prefill
        ? `Trusted ${r.identity.repository} · ${r.identity.workflow_path}. Run the workflow again in GitHub Actions; the check will count.`
        : `Trusted ${r.identity.repository} · ${r.identity.workflow_path}. Runs from it count from now on.`;
      // V112-3-4 (#230 Medium): the visible success panel shows on every save, not only a prefilled one.
      setDone(text);
      say(text);
      focusNext.current = `ci-id-stop-${r.identity.identity_id}`;
    } catch (e2) {
      const b = e2 instanceof ApiError && e2.body && typeof e2.body === 'object' ? e2.body as { error?: string; detail?: string; field?: keyof Fields } : {};
      setConfirm(null);
      if (prefill && e2 instanceof ApiError && e2.status === 409) {
        // Already trusted (e.g. trusted from another tab): the next step is the same — run it again.
        const text = `${values.repository} · ${values.workflow_path} is already trusted. Run the workflow again in GitHub Actions; the check will count.`;
        setAdding(false); setDone(text); say(text); focusNext.current = 'ci-id-done';
        return;
      }
      if (b.field) { const id = `ci-id-${b.field === 'repository' ? 'repo' : b.field === 'workflow_path' ? 'wf' : 'ref'}`; setErrors({ [b.field]: b.error }); window.setTimeout(() => document.getElementById(id)?.focus(), 0); }
      else setErr(b.detail ?? b.error ?? 'The workflow could not be trusted.');
    } finally { setBusy(null); }
  };
  const remove = async (row: CiIdentity, e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) { setReasonError('Say why you stop trusting it.'); document.getElementById(`ci-id-why-${row.identity_id}`)?.focus(); return; }
    setBusy(row.identity_id); setErr(null); setReasonError('');
    // A-H3: after removal, focus the next trusted row's button, else "Trust a workflow".
    const others = (q.data?.identities ?? []).filter((x) => x.status === 'active' && x.identity_id !== row.identity_id);
    try {
      await disableCiIdentity(slug, row.identity_id, reason.trim());
      await qc.invalidateQueries({ queryKey: ['ci-identities', slug] });
      setRemoving(null); setReason('');
      say(`${row.repository} · ${row.workflow_path} is no longer trusted.`);
      focusNext.current = others[0] ? `ci-id-stop-${others[0].identity_id}` : 'ci-id-add';
    } catch (e2) {
      const b = e2 instanceof ApiError && e2.body && typeof e2.body === 'object' ? e2.body as { error?: string; detail?: string } : {};
      setErr(b.detail ?? b.error ?? 'It could not be removed.');
    } finally { setBusy(null); }
  };

  const rows = q.data?.identities ?? [];
  const active = rows.filter((r) => r.status === 'active');
  const history = rows.filter((r) => r.status !== 'active');
  const set = (k: keyof Fields) => (e: { currentTarget: HTMLInputElement }) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, [k]: v })); setErrors((x) => ({ ...x, [k]: undefined })); };

  return (
    <div id="ci-identities" className="dps" tabIndex={-1} role="region" aria-labelledby="ci-id-h">
      <Card><div className="dps-body">
        <h2 id="ci-id-h" className="dps-h">CI identities</h2>
        <p className="dps-dim" id="ci-id-what">The CI workflows GAC Pro trusts to run verification checks and sign CI gate results for your systems. Runs from any other workflow or branch are recorded as not confirmed.</p>
        <p role="status" className="sr-only">{msg}</p>
        {done ? (
          <div id="ci-id-done" tabIndex={-1} className="dps-confirm" role="group" aria-label="Workflow trusted">
            <p>{done}</p>
            {returnTo ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="primary" to={returnTo}>Back to the target plan</Link> : null}
          </div>
        ) : null}
        {err ? <Alert variant="error"><p>{err}</p></Alert> : null}
        {q.isLoading ? <Spinner label="Loading CI identities" /> : q.error ? (
          <Alert variant="error"><p>CI identities could not load.</p><Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
        ) : (
          <>
            {active.length ? (
              <ul className="dps-list" aria-label="Trusted workflows">
                {active.map((r) => (
                  <li key={r.identity_id} className="dps-row">
                    <span><b>{r.repository}</b>{` · ${r.workflow_path.replace(/^\.github\/workflows\//, '')} · ${r.ref.replace(/^refs\/heads\//, '')}`}<span className="dps-dim">{` · GitHub Actions · since ${fmtDate(r.created_at)}${r.ref.startsWith('refs/heads/') ? '' : ' · checks only'}`}</span></span>
                    {removing === r.identity_id ? (
                      <form onSubmit={(e) => void remove(r, e)} noValidate aria-label={`Stop trusting ${r.repository}`} data-esc-owner=""
                        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setRemoving(null); setReasonError(''); focusNext.current = `ci-id-stop-${r.identity_id}`; } }}>
                        <Field id={`ci-id-why-${r.identity_id}`} label="Why" required error={reasonError || undefined}>
                          <Input autoFocus value={reason} maxLength={500} aria-invalid={reasonError ? true : undefined} onChange={(e) => { setReason(e.currentTarget.value); setReasonError(''); }} />
                        </Field>
                        <span className="dps-row">
                          <Button compact variant="danger" type="submit" aria-busy={busy === r.identity_id || undefined}>{busy === r.identity_id ? 'Removing…' : 'Stop trusting it'}</Button>
                          <Button compact variant="ghost" type="button" onClick={() => { setRemoving(null); setReasonError(''); focusNext.current = `ci-id-stop-${r.identity_id}`; }}>Cancel</Button>
                        </span>
                      </form>
                    ) : <Button compact variant="ghost" id={`ci-id-stop-${r.identity_id}`} aria-label={`Stop trusting ${r.repository} · ${r.workflow_path}`} onClick={() => { setRemoving(r.identity_id); setReason(''); }}>Stop trusting</Button>}
                  </li>
                ))}
              </ul>
            ) : !adding ? <p>No CI workflow is trusted yet.</p> : null}
            {adding && confirm ? (
              <div className="dps-confirm" role="group" aria-labelledby="ci-id-confirm-h" aria-describedby="ci-id-what" data-esc-owner=""
                onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setConfirm(null); setAdding(false); focusNext.current = 'ci-id-add'; } }}>
                <h3 id="ci-id-confirm-h" className="dps-h" tabIndex={-1}>Trust exactly this workflow?</h3>
                <dl className="dps-dl">
                  <dt>Repository</dt><dd><b>{confirm.repository}</b></dd>
                  <dt>Workflow file</dt><dd><code>{confirm.workflow_path}</code></dd>
                  <dt>Runs from</dt><dd><code>{fullRef(confirm.ref)}</code></dd>
                  <dt>CI</dt><dd>GitHub Actions</dd>
                  <dt>Trusted for</dt><dd>{fullRef(confirm.ref).startsWith('refs/heads/') ? 'Signed runs of this file: checks and CI gate results, all systems' : 'Verification checks only. CI gate results need a branch.'}</dd>
                </dl>
                {prefill ? <p className="dps-dim">Filled in from a link. Check the owner and repository name letter by letter.</p> : null}
                <span className="dps-row">
                  <Button compact variant="primary" type="button" onClick={() => void trust(confirm)} aria-busy={busy === 'add' || undefined}>{busy === 'add' ? 'Trusting…' : 'Yes, trust it'}</Button>
                  <Button compact variant="ghost" type="button" onClick={() => { setConfirm(null); window.setTimeout(() => document.getElementById('ci-id-repo')?.focus(), 0); }}>Back</Button>
                  <Button compact variant="ghost" type="button" onClick={() => { setConfirm(null); setAdding(false); focusNext.current = 'ci-id-add'; }}>Cancel</Button>
                </span>
              </div>
            ) : adding ? (
              <form onSubmit={(e) => void submit(e)} noValidate aria-label="Trust a GitHub workflow" aria-describedby="ci-id-what" data-esc-owner=""
                onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setAdding(false); focusNext.current = 'ci-id-add'; } }}>
                <Field id="ci-id-repo" label="Repository" required hint="owner/name, e.g. acme/infra" error={errors.repository}>
                  <Input value={form.repository} autoComplete="off" onChange={set('repository')} />
                </Field>
                <Field id="ci-id-wf" label="Workflow file" required hint="e.g. .github/workflows/governance.yml" error={errors.workflow_path}>
                  <Input value={form.workflow_path} autoComplete="off" onChange={set('workflow_path')} />
                </Field>
                <Field id="ci-id-ref" label="Branch" required hint={refSwapped ? 'Changed to main: CI gate results count from your default branch.' : 'Your default branch, e.g. main.'} error={errors.ref}>
                  <Input value={form.ref} autoComplete="off" onChange={set('ref')} />
                </Field>
                <span className="dps-row">
                  <Button compact variant="primary" type="submit">Review and trust</Button>
                  <Button compact variant="ghost" type="button" onClick={() => { setAdding(false); focusNext.current = 'ci-id-add'; }}>Cancel</Button>
                </span>
              </form>
            ) : <Button compact id="ci-id-add" variant={active.length ? 'secondary' : 'primary'} onClick={() => { setAdding(true); window.setTimeout(() => document.getElementById('ci-id-repo')?.focus(), 0); }}>Trust a workflow</Button>}
            {history.length ? (
              <details className="dps-dim"><summary>{`No longer trusted (${history.length})`}</summary>
                <ul>{history.map((r) => <li key={r.identity_id}>{`${r.repository} · ${r.workflow_path.replace(/^\.github\/workflows\//, '')} · ${r.ref.replace(/^refs\/heads\//, '')} — removed ${fmtDate(r.disabled_at!)}: ${r.disabled_reason ?? ''}`}</li>)}</ul>
              </details>
            ) : null}
          </>
        )}
      </div></Card>
    </div>
  );
}

/** The full ref GAC Pro will match (the server's normalizeRef): a branch name means refs/heads/<name>. */
function fullRef(ref: string): string { return ref.startsWith('refs/') ? ref : `refs/heads/${ref}`; }
